// Display names for the identity row facts the transcript response carries
// only as ids, plus the viewer id for display preferences.

import { useQuery } from "@tanstack/react-query";

import type { components } from "../../api/schema";
import { apiClient } from "../../lib/apiClient";
import { ApiRequestError } from "../../lib/errors";
import { isShowcaseMode } from "../../lib/showcaseMode";
import { queryKeys } from "../../lib/queryKeys";

type TranscriptResponse = components["schemas"]["TranscriptResponse"];

export function useTranscriptIdentity(
  transcript: TranscriptResponse | null | undefined,
): {
  agentVersionTag: string | null;
  batchName: string | null;
  viewerId?: string;
} {
  const me = useQuery({
    enabled: !isShowcaseMode,
    queryKey: queryKeys.me,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/me");
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });
  const batchId = transcript?.batch_id ?? null;
  const agentVersionId = transcript?.agent_version_id ?? null;
  const batch = useQuery({
    enabled: batchId != null,
    queryKey: queryKeys.batches.detail(batchId ?? ""),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/batches/{id}", {
        params: { path: { id: batchId ?? "" } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });
  const agentVersion = useQuery({
    enabled: agentVersionId != null,
    queryKey: queryKeys.agentVersions.detail(agentVersionId ?? ""),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/agent-versions/{id}", {
        params: { path: { id: agentVersionId ?? "" } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });
  return {
    viewerId: isShowcaseMode ? undefined : me.data?.user.id,
    agentVersionTag: agentVersion.data?.tag ?? null,
    batchName: batch.data?.display_name ?? null,
  };
}
